import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import {
  Building2,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { useAdminCompanies, type CompanyQueryParams } from '../../../hooks/useCompanyRegistration';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Rascunho', color: 'bg-surface-600 text-surface-300' },
  DOCUMENTS_UPLOADED: { label: 'Documentos Enviados', color: 'bg-blue-500/20 text-blue-400' },
  PENDING_COMPLIANCE: { label: 'Em Análise', color: 'bg-yellow-500/20 text-yellow-400' },
  APPROVED: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  REJECTED: { label: 'Rejeitado', color: 'bg-red-500/20 text-red-400' },
  PENDING_ADDITIONAL_DOCS: { label: 'Docs Adicionais', color: 'bg-orange-500/20 text-orange-400' },
  ACTIVE: { label: 'Ativo', color: 'bg-emerald-500/20 text-emerald-400' },
};

const ALL_STATUSES = Object.keys(STATUS_LABELS);

function formatCnpj(cnpj: string) {
  const d = cnpj.replace(/\D/g, '');
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

export function CompanyList() {
  const navigate = useNavigate();
  const [query, setQuery] = useState<CompanyQueryParams>({ page: 1, limit: 10 });
  const [searchInput, setSearchInput] = useState('');
  const { data, isLoading } = useAdminCompanies(query);

  const companies = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / (query.limit || 10));

  const handleSearch = () => {
    setQuery(prev => ({ ...prev, search: searchInput, page: 1 }));
  };

  // Count by status
  const statusCounts = companies.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Building2 size={24} className="text-primary-400" />
          Empresas PJ
        </h1>
        <p className="text-surface-400 mt-1">Gerencie as empresas cadastradas no seu tenant.</p>
      </div>

      {/* Status Counters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setQuery(prev => ({ ...prev, status: undefined, page: 1 }))}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            !query.status ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500' : 'bg-surface-800 text-surface-400 hover:text-white'
          }`}
        >
          Todos ({total})
        </button>
        {ALL_STATUSES.map(s => {
          const info = STATUS_LABELS[s];
          const count = statusCounts[s] || 0;
          return (
            <button key={s}
              onClick={() => setQuery(prev => ({ ...prev, status: s, page: 1 }))}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                query.status === s ? `${info.color} ring-1 ring-current` : 'bg-surface-800 text-surface-400 hover:text-white'
              }`}
            >
              {info.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="flex-1">
          <Input placeholder="Buscar por nome ou CNPJ..." value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()} />
        </div>
        <Button variant="secondary" onClick={handleSearch}>
          <Search size={16} />
        </Button>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-800">
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Nome</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">CNPJ</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Data Cadastro</th>
                <th className="text-right text-xs font-medium text-surface-400 px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} className="text-center py-8 text-surface-400">Carregando...</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-surface-400">Nenhuma empresa encontrada.</td></tr>
              ) : companies.map(company => {
                const statusInfo = STATUS_LABELS[company.status] || { label: company.status, color: 'bg-surface-700 text-surface-300' };
                return (
                  <tr key={company.id} className="border-b border-surface-800/50 hover:bg-surface-800/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-white">{company.companyName}</td>
                    <td className="px-4 py-3 text-sm text-surface-300 font-mono">{formatCnpj(company.companyCnpj)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-surface-400">{formatDate(company.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => navigate(`/admin/companies/${company.id}`)}
                        className="text-primary-400 hover:text-primary-300 transition-colors">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-800">
            <span className="text-xs text-surface-400">
              Página {query.page} de {totalPages} ({total} empresas)
            </span>
            <div className="flex gap-1">
              <button disabled={query.page === 1}
                onClick={() => setQuery(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                className="p-1 rounded text-surface-400 hover:text-white disabled:opacity-30">
                <ChevronLeft size={16} />
              </button>
              <button disabled={query.page === totalPages}
                onClick={() => setQuery(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                className="p-1 rounded text-surface-400 hover:text-white disabled:opacity-30">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
